import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import {
  getPatrolAssetHotspotShowTypeLabel,
  getWebsiteHotspotHostname,
  getYoutubeEmbedUrl,
  isIframeLoadBlockedErrorFrame,
  normalizeWebsiteHotspotUrl,
} from "./patrolAssetHotspotContent";
import { PatrolApiUrlHotspotToggle } from "./PatrolApiUrlHotspotToggle.jsx";
import { PatrolHotspotPdfPanel } from "./PatrolHotspotPdfPanel.jsx";

const POPUP_SIZES = {
  image: { width: 280, maxHeight: 340 },
  youtube: { width: 360, maxHeight: 280 },
  pdf: { width: 520, maxHeight: 420 },
  website: { width: 480, maxHeight: 400 },
  text: { width: 320, maxHeight: 280 },
  api_url: { width: 300, maxHeight: 200 },
};

function getHotspotPopupContentUrl(hotspot) {
  const showType = String(hotspot?.showType ?? "").trim().toLowerCase();
  const content =
    hotspot?.contentUrl ?? hotspot?.content_url ?? "";
  if (showType === "api_url") {
    return String(content).trim();
  }
  return String(content || hotspot?.imageUrl || "").trim();
}

function WebsiteHotspotFooter({ href, onOpenExternal }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        px: 1,
        py: 0.5,
        borderTop: "1px solid rgba(0,0,0,0.08)",
        bgcolor: "#f9fafb",
      }}
    >
      <Typography
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        noWrap
        sx={{
          fontSize: 11,
          color: "#2563EB",
          flex: 1,
          minWidth: 0,
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {href}
      </Typography>
      <Button
        size="small"
        variant="text"
        startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
        onClick={onOpenExternal}
        sx={{ textTransform: "none", flexShrink: 0, fontSize: 12 }}
      >
        New tab
      </Button>
    </Box>
  );
}

function WebsiteHotspotPanel({ url, title, onOpenExternal }) {
  const href = normalizeWebsiteHotspotUrl(url);
  const host = getWebsiteHotspotHostname(url);
  const [embedBlocked, setEmbedBlocked] = useState(false);
  const iframeRef = useRef(null);
  const frameHeight = POPUP_SIZES.website.maxHeight - 44;
  const siteLabel = host || "This site";

  useEffect(() => {
    setEmbedBlocked(false);
  }, [href]);

  const detectEmbedBlocked = useCallback(() => {
    if (isIframeLoadBlockedErrorFrame(iframeRef.current)) {
      setEmbedBlocked(true);
    }
  }, []);

  const handleIframeLoad = useCallback(() => {
    detectEmbedBlocked();
    window.setTimeout(detectEmbedBlocked, 0);
    window.setTimeout(detectEmbedBlocked, 200);
  }, [detectEmbedBlocked]);

  if (!href) {
    return (
      <Typography sx={{ fontSize: 13, color: "#6B7280", p: 2 }}>
        No website URL configured.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: frameHeight,
          minHeight: 260,
          bgcolor: "#fff",
        }}
      >
        <Box
          ref={iframeRef}
          component="iframe"
          src={href}
          title={title || "Website"}
          referrerPolicy="no-referrer-when-downgrade"
          onLoad={handleIframeLoad}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            border: "none",
            bgcolor: "#fff",
            visibility: embedBlocked ? "hidden" : "visible",
          }}
        />
        {embedBlocked ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              px: 2,
              py: 2,
              bgcolor: "#f8fafc",
              zIndex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                color: "#374151",
                textAlign: "center",
                lineHeight: 1.5,
              }}
            >
              <strong>{siteLabel}</strong> cannot be shown in the popup (site blocks
              iframes). Use <strong>Open in browser</strong> instead.
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={onOpenExternal}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Open {title || "website"}
            </Button>
          </Box>
        ) : null}
      </Box>
      <WebsiteHotspotFooter href={href} onOpenExternal={onOpenExternal} />
    </Box>
  );
}

function HotspotPopupContent({ hotspot, onOpenExternal }) {
  const showType = hotspot?.showType || "image";
  const url = getHotspotPopupContentUrl(hotspot);

  if (!url) {
    return (
      <Typography sx={{ fontSize: 13, color: "#6B7280", p: 2 }}>
        No content URL configured.
      </Typography>
    );
  }

  if (showType === "image") {
    return (
      <Box
        component="img"
        src={url}
        alt={hotspot?.assetName || hotspot?.assetItem || "Asset"}
        sx={{
          display: "block",
          width: "100%",
          maxHeight: POPUP_SIZES.image.maxHeight - 8,
          objectFit: "contain",
          bgcolor: "#f8fafc",
        }}
      />
    );
  }

  if (showType === "pdf") {
    return (
      <PatrolHotspotPdfPanel
        url={url}
        title={hotspot?.assetName || hotspot?.assetItem}
        frameHeight={POPUP_SIZES.pdf.maxHeight - 40}
        onOpenExternal={onOpenExternal}
      />
    );
  }

  if (showType === "text") {
    return (
      <Typography
        component="div"
        sx={{
          fontSize: 13,
          lineHeight: 1.5,
          color: "#374151",
          p: 2,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {url}
      </Typography>
    );
  }

  if (showType === "api_url") {
    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <PatrolApiUrlHotspotToggle contentUrl={url} />
      </Box>
    );
  }

  if (showType === "website") {
    return (
      <WebsiteHotspotPanel
        url={url}
        title={hotspot?.assetName || hotspot?.assetItem}
        onOpenExternal={onOpenExternal}
      />
    );
  }

  if (showType === "youtube") {
    const embed = getYoutubeEmbedUrl(url);
    if (!embed) {
      return (
        <Typography sx={{ fontSize: 13, color: "#B91C1C", p: 2 }}>
          Invalid YouTube URL.
        </Typography>
      );
    }
    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          pt: "56.25%",
          bgcolor: "#000",
        }}
      >
        <Box
          component="iframe"
          src={embed}
          title={hotspot?.assetName || "YouTube"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: "none",
          }}
        />
      </Box>
    );
  }

  return (
    <Typography sx={{ fontSize: 13, color: "#6B7280", p: 2 }}>
      Unsupported content type.
    </Typography>
  );
}

/**
 * Floating content card over the 360° viewer (image / PDF / website / YouTube / API URL).
 */
export const PatrolAssetHotspotFloatingPopup = memo(
  function PatrolAssetHotspotFloatingPopup({
    hotspot,
    /** When true, popup is positioned above the parent hotspot (moves with pan/zoom). */
    anchored = true,
    onClose,
  }) {
    const showType = hotspot?.showType || "image";
    const openUrl =
      showType === "api_url" ? "" : getHotspotPopupContentUrl(hotspot);
    const typeLabel = getPatrolAssetHotspotShowTypeLabel(showType);

    const size = useMemo(() => {
      const base = POPUP_SIZES[showType] || POPUP_SIZES.image;
      return {
        width: typeof base.width === "number" ? base.width : 360,
        maxHeight: typeof base.maxHeight === "number" ? base.maxHeight : 320,
      };
    }, [showType]);

    const handleOpenExternal = useCallback(() => {
      const target =
        showType === "website"
          ? normalizeWebsiteHotspotUrl(openUrl)
          : openUrl;
      if (target) window.open(target, "_blank", "noopener,noreferrer");
    }, [openUrl, showType]);

    const handleFullscreen = useCallback(() => {
      if (!openUrl) return;
      window.open(openUrl, "_blank", "noopener,noreferrer");
    }, [openUrl]);

    const title =
      hotspot?.assetName || hotspot?.assetItem || typeLabel;

    return (
      <Box
        role="dialog"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          position: "absolute",
          ...(anchored
            ? {
                left: "50%",
                bottom: "100%",
                transform: "translateX(-50%)",
                mb: 1.25,
              }
            : {
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -100%)",
              }),
          width: size.width,
          maxWidth: "min(92vw, 560px)",
          maxHeight: size.maxHeight,
          zIndex: 25,
          pointerEvents: "auto",
          bgcolor: "#fff",
          borderRadius: "6px",
          overflow: "hidden",
          boxShadow:
            "0 12px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.08)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 1,
            py: 0.5,
            minHeight: 32,
            bgcolor: showType === "youtube" ? "#0f0f0f" : "#fff",
            borderBottom:
              showType === "image" ? "none" : "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Typography
            noWrap
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: showType === "youtube" ? "#fff" : "#111827",
              flex: 1,
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            {openUrl && showType !== "text" && showType !== "api_url" ? (
              <IconButton
                size="small"
                onClick={handleOpenExternal}
                aria-label="Open in new tab"
                sx={{
                  color: showType === "youtube" ? "#fff" : "#4B5563",
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 18 }} />
              </IconButton>
            ) : null}
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close"
              sx={{
                color: showType === "youtube" ? "#fff" : "#4B5563",
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ position: "relative", flex: 1, minHeight: 0, overflow: "auto" }}>
          <HotspotPopupContent
            hotspot={hotspot}
            onOpenExternal={handleOpenExternal}
          />
          {showType === "image" && openUrl ? (
            <IconButton
              size="small"
              onClick={handleFullscreen}
              aria-label="Expand"
              sx={{
                position: "absolute",
                right: 6,
                bottom: 6,
                bgcolor: "rgba(255,255,255,0.92)",
                boxShadow: 1,
                "&:hover": { bgcolor: "#fff" },
              }}
            >
              <FullscreenIcon sx={{ fontSize: 18 }} />
            </IconButton>
          ) : null}
        </Box>
      </Box>
    );
  },
);
